import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ChartBar, ArrowsClockwise, Users, Database, Lightning,
  LockSimple, Play, Bank, CheckCircle, Plus, Wrench
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_da191abd-9276-45a7-bd48-1757c1f4e471/artifacts/p5urxzpf_diametor_logo_black__1_-removebg-preview.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_debt-cycle/artifacts/gzyrfqiy_diametor_logo_white-removebg-preview.png";
const CUR = '\u20B9';

function StatCard({ label, value, sub, isDark, accent }) {
  return (
    <div className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
      <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{label}</p>
      <p className={`font-outfit text-2xl font-bold mt-1 ${accent || (isDark ? 'text-zinc-50' : 'text-zinc-950')}`}>{value}</p>
      {sub && <p className={`font-ibm text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{sub}</p>}
    </div>
  );
}

const feeStatusColor = (s) => ({
  paid: 'text-green-500', charged: 'text-green-500', mandate_active: 'text-blue-400',
  mandate_pending: 'text-yellow-400', pending: 'text-orange-400',
}[s] || 'text-zinc-400');

export default function AdminAuditPanel() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [isAdmin, setIsAdmin] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [nettingCycles, setNettingCycles] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [pool, setPool] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [execErrors, setExecErrors] = useState({});
  const [participantsOpen, setParticipantsOpen] = useState(null);
  const [participantsData, setParticipantsData] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [maint, setMaint] = useState({ enabled: false, until: '', message: '' });
  const [savingMaint, setSavingMaint] = useState(false);
  const [loans, setLoans] = useState([]);
  const [allowLoans, setAllowLoans] = useState(true);
  const [savingLoanCfg, setSavingLoanCfg] = useState(false);
  const [payLoan, setPayLoan] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', reference: '', particulars: '', paid_date: '', paid_time: '', mode: 'Bank Transfer' });
  const [payingLoan, setPayingLoan] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [refundItem, setRefundItem] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', reference: '', particulars: '', paid_date: '', paid_time: '', mode: 'Bank Transfer' });
  const [confirmingRefund, setConfirmingRefund] = useState(false);

  useEffect(() => {
    axios.get(`${API}/support/check-admin`, { withCredentials: true })
      .then(res => setIsAdmin(res.data.is_admin)).catch(() => setIsAdmin(false));
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, ncRes, ndRes, poolRes, balRes, mRes, lRes, lcRes, rfRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { withCredentials: true }),
        axios.get(`${API}/admin/netting-cycles`, { withCredentials: true }),
        axios.get(`${API}/admin/nodes`, { withCredentials: true }),
        axios.get(`${API}/admin/pool`, { withCredentials: true }),
        axios.get(`${API}/admin/user-balances`, { withCredentials: true }),
        axios.get(`${API}/admin/maintenance`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/loans`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/loans-config`, { withCredentials: true }).catch(() => ({ data: { allow_loans: true } })),
        axios.get(`${API}/admin/refunds`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes.data);
      setNettingCycles(ncRes.data);
      setNodes(ndRes.data);
      setPool(poolRes.data);
      setBalances(balRes.data);
      setLoans(lRes.data || []);
      setAllowLoans(!!lcRes.data.allow_loans);
      setRefunds(rfRes.data || []);
      const m = mRes.data || {};
      setMaint({
        enabled: !!m.enabled,
        until: m.until ? new Date(m.until).toISOString().slice(0, 16) : '',
        message: m.message || '',
      });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) fetchAll(); }, [isAdmin, fetchAll]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/engine/trigger`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Discovery complete');
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setTriggering(false); }
  };

  const handleExecute = async (netting_id) => {
    try {
      const res = await axios.post(`${API}/admin/cycles/${netting_id}/execute`, {}, { withCredentials: true });
      if (res.data.ok === false) {
        setExecErrors(prev => ({ ...prev, [netting_id]: res.data }));
        toast.error(res.data.error || 'Execution blocked');
        return;
      }
      setExecErrors(prev => { const n = { ...prev }; delete n[netting_id]; return n; });
      toast.success(`Executed — cleared ${'\u20B9'}${(res.data.total_cleared || 0).toLocaleString()}`);
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Execution failed'); }
  };

  const viewParticipants = async (netting_id) => {
    setParticipantsOpen(netting_id);
    setParticipantsData(null);
    try {
      const res = await axios.get(`${API}/admin/cycles/${netting_id}/participants`, { withCredentials: true });
      setParticipantsData(res.data);
    } catch { toast.error('Failed to load participants'); }
  };

  const openUserDetail = async (user_id) => {
    setUserDetailOpen(user_id);
    setUserDetail(null);
    try {
      const res = await axios.get(`${API}/admin/users/${user_id}/detail`, { withCredentials: true });
      setUserDetail(res.data);
    } catch { toast.error('Failed to load user details'); }
  };

  const handleSaveMaintenance = async (override) => {
    const next = { ...maint, ...(override || {}) };
    setSavingMaint(true);
    try {
      await axios.post(`${API}/admin/maintenance`, {
        enabled: next.enabled,
        until: next.until ? new Date(next.until).toISOString() : null,
        message: next.message || null,
      }, { withCredentials: true });
      setMaint(next);
      toast.success(next.enabled ? 'Maintenance mode ENABLED — users are frozen' : 'Maintenance mode disabled');
    } catch { toast.error('Failed to update maintenance mode'); }
    finally { setSavingMaint(false); }
  };

  const toggleLoans = async () => {
    setSavingLoanCfg(true);
    try {
      const res = await axios.post(`${API}/admin/loans-config`, { enabled: !allowLoans }, { withCredentials: true });
      setAllowLoans(res.data.allow_loans);
      toast.success(res.data.allow_loans ? 'Loans ENABLED for users' : 'Loans disabled — the loan box is hidden for users');
    } catch { toast.error('Failed to update loans setting'); }
    finally { setSavingLoanCfg(false); }
  };

  const openPayLoan = (loan) => {
    const now = new Date();
    setPayForm({
      amount: String(loan.loan_amount || ''), reference: '', particulars: '',
      paid_date: now.toISOString().slice(0, 10), paid_time: now.toTimeString().slice(0, 5),
      mode: 'Bank Transfer',
    });
    setPayLoan(loan);
  };

  const submitPayLoan = async () => {
    if (!payForm.reference.trim()) { toast.error('Transaction reference is required'); return; }
    setPayingLoan(true);
    try {
      await axios.post(`${API}/admin/loans/${payLoan.loan_id}/pay`, payForm, { withCredentials: true });
      toast.success('Loan closed — Certificate of Loan Closure issued');
      setPayLoan(null);
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to close loan'); }
    finally { setPayingLoan(false); }
  };

  const openRefund = (r) => {
    const now = new Date();
    setRefundForm({ amount: String(r.amount || ''), reference: '', particulars: '',
      paid_date: now.toISOString().slice(0, 10), paid_time: now.toTimeString().slice(0, 5), mode: 'Bank Transfer' });
    setRefundItem(r);
  };

  const submitRefund = async () => {
    if (!refundForm.reference.trim()) { toast.error('Transaction reference is required'); return; }
    setConfirmingRefund(true);
    try {
      await axios.post(`${API}/admin/refunds/${refundItem.refund_id}/confirm`, refundForm, { withCredentials: true });
      toast.success('Refund confirmed');
      setRefundItem(null);
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to confirm refund'); }
    finally { setConfirmingRefund(false); }
  };

  const handleTopup = async () => {    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await axios.post(`${API}/admin/pool/topup`, { amount: amt, source: 'Top-up from collected fees' }, { withCredentials: true });
      toast.success('Pool topped up');
      setTopupAmount('');
      fetchAll();
    } catch { toast.error('Top-up failed'); }
  };

  if (isAdmin === null || loading) {
    return <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-white'}`}><div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" /></div>;
  }
  if (!isAdmin) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="audit-access-denied">
        <div className="text-center max-w-sm px-6">
          <LockSimple size={48} weight="bold" className={`mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-zinc-300'}`} />
          <h2 className={`font-outfit text-xl font-bold mb-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Access Denied</h2>
          <button onClick={() => navigate('/dashboard')} className="h-10 px-6 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500" data-testid="audit-back-btn">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const d = dashboard || {};

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="admin-audit-panel">
      <div className={`py-4 px-6 border-b flex items-center justify-between ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
        <div className="flex items-center gap-3">
          <img src={isDark ? LOGO_DARK : LOGO_LIGHT} alt="Diametor" className="h-7" />
          <span className={`font-ibm text-xs font-semibold uppercase tracking-[0.2em] px-2 py-1 rounded-sm ${isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-100 text-red-700'}`}>Admin</span>
          {d.test_mode && <span className="font-ibm text-[10px] px-2 py-1 rounded-sm bg-yellow-400/10 text-yellow-400">TEST MODE</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleTrigger} disabled={triggering} className="h-8 px-4 bg-yellow-400 text-zinc-950 font-ibm text-xs font-semibold rounded-sm hover:bg-yellow-500 disabled:opacity-50 flex items-center gap-1" data-testid="trigger-engine-btn">
            <Play size={12} weight="bold" /> {triggering ? 'Running...' : 'Run Engine'}          </button>
          <button onClick={() => navigate('/dashboard')} className={`h-8 px-4 font-ibm text-xs rounded-sm border ${isDark ? 'border-zinc-700 text-zinc-400' : 'border-zinc-200 text-zinc-600'}`} data-testid="audit-dashboard-btn">Dashboard</button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <Tabs defaultValue="overview">
          <TabsList className={`mb-6 flex-wrap ${isDark ? 'bg-zinc-900' : 'bg-zinc-100'}`}>
            <TabsTrigger value="overview" className="font-ibm text-xs gap-1"><ChartBar size={14} /> Overview</TabsTrigger>
            <TabsTrigger value="cycles" className="font-ibm text-xs gap-1"><ArrowsClockwise size={14} /> Netting Cycles ({nettingCycles.length})</TabsTrigger>
            <TabsTrigger value="nodes" className="font-ibm text-xs gap-1"><Users size={14} /> Participants ({nodes.length})</TabsTrigger>
            <TabsTrigger value="pool" className="font-ibm text-xs gap-1"><Bank size={14} /> Pool</TabsTrigger>
            <TabsTrigger value="balances" className="font-ibm text-xs gap-1"><Database size={14} /> Balances</TabsTrigger>
            <TabsTrigger value="loans" className="font-ibm text-xs gap-1" data-testid="admin-tab-loans"><Bank size={14} /> Loans ({loans.length})</TabsTrigger>
            <TabsTrigger value="refunds" className="font-ibm text-xs gap-1" data-testid="admin-tab-refunds"><Wrench size={14} /> Refunds ({refunds.filter(r => r.status !== 'completed').length})</TabsTrigger>
            <TabsTrigger value="maintenance" className="font-ibm text-xs gap-1"><Wrench size={14} /> Maintenance</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard label="Total Users" value={d.total_users || 0} isDark={isDark} />
              <StatCard label="Participants (Nodes)" value={d.total_nodes || 0} sub={`Min cycle: ${d.min_participants}`} isDark={isDark} accent="text-blue-400" />
              <StatCard label="Fee Paid" value={d.nodes_paid || 0} isDark={isDark} accent="text-green-500" />
              <StatCard label="Mandate Active" value={d.nodes_mandate_active || 0} isDark={isDark} accent="text-yellow-400" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard label="Netting Cycles" value={d.total_netting_cycles || 0} isDark={isDark} accent="text-green-500" />
              <StatCard label="Total Cleared" value={`${CUR}${(d.total_cleared || 0).toLocaleString()}`} isDark={isDark} />
              <StatCard label="Profit (Fees)" value={`${CUR}${(d.total_profit || 0).toLocaleString()}`} isDark={isDark} accent="text-green-500" />
              <StatCard label="System Income" value={`${CUR}${(d.system_income || 0).toLocaleString()}`} isDark={isDark} accent="text-green-500" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Pool Balance" value={`${CUR}${(d.pool_balance || 0).toLocaleString()}`} isDark={isDark} accent="text-yellow-400" />
              <StatCard label="KYC Verified" value={d.kyc_verified || 0} isDark={isDark} />
              <StatCard label="User Balance Held" value={`${CUR}${(d.total_user_balance || 0).toLocaleString()}`} isDark={isDark} />
              <StatCard label="Last Paid" value={d.last_paid || '—'} sub={d.last_cycle_at ? new Date(d.last_cycle_at).toLocaleString() : ''} isDark={isDark} accent="text-blue-400" />
            </div>
            <div className="mt-6">
              <h3 className={`font-outfit text-base font-semibold mb-3 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Recent Engine Runs</h3>
              <div className="space-y-2">
                {(d.last_engine_runs || []).length === 0 ? (
                  <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No engine runs yet. Click "Run Engine".</p>
                ) : (d.last_engine_runs || []).map(r => (
                  <div key={r.run_id} className={`p-3 border rounded-md flex items-center justify-between ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
                    <div className="flex items-center gap-2">
                      <Lightning size={14} className="text-yellow-400" />
                      <span className={`font-ibm text-xs font-mono ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{r.run_id}</span>
                      <span className={`font-ibm text-[10px] ${r.status === 'completed' ? 'text-green-500' : r.status === 'failed' ? 'text-red-400' : 'text-yellow-400'}`}>{r.status}</span>
                    </div>
                    <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{r.cycles_found || 0} cycles · {r.settlements_made || 0} edges · {CUR}{(r.total_cleared || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* NETTING CYCLES */}
          <TabsContent value="cycles">
            {(() => {
              const proposed = nettingCycles.filter(c => c.status === 'proposed');
              const executed = nettingCycles.filter(c => c.status === 'executed' || !c.status);
              const renderCycle = (c, isProposed) => (
                <div key={c.netting_id} className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`netting-${c.netting_id}`}>
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{c.participants_count} participants</span>
                        <span className={`font-ibm text-[10px] px-1.5 py-0.5 rounded-sm ${c.is_cycle ? 'bg-blue-500/10 text-blue-400' : 'bg-green-500/10 text-green-500'}`}>{c.is_cycle ? 'Closed Loop' : 'Pool-funded Path'}</span>
                        {isProposed
                          ? <span className="font-ibm text-[10px] px-1.5 py-0.5 rounded-sm bg-yellow-400/10 text-yellow-400">Proposed</span>
                          : <span className="font-ibm text-[10px] px-1.5 py-0.5 rounded-sm bg-green-500/10 text-green-500">Executed</span>}
                      </div>
                      {!isProposed && <p className={`font-ibm text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Paid last: <span className="font-semibold">{c.paid_last || '—'}</span></p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-outfit text-lg font-bold text-yellow-400">{CUR}{((isProposed ? c.estimated_cleared : c.total_cleared) || 0).toLocaleString()}</p>
                      <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>flow {CUR}{(c.flow_amount || 0).toLocaleString()} · profit {CUR}{(c.profit || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {(() => {
                      const parts = c.participants || [];
                      const shown = parts.length > 4
                        ? [parts[0], { name: `… +${parts.length - 2} more …`, _dots: true }, parts[parts.length - 1]]
                        : parts;
                      return shown.map((p, i) => (
                        <span key={i} className={`font-ibm text-[10px] px-2 py-1 rounded-sm ${p._dots ? (isDark ? 'bg-transparent text-zinc-500' : 'bg-transparent text-zinc-400') : (p.user_id === c.endpoint_user_id ? 'bg-green-500/15 text-green-500 font-semibold' : (isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-200 text-zinc-700'))}`} title={p.email}>
                          {p.name}{p.user_id === c.endpoint_user_id && !isProposed ? ' (paid)' : ''}{i < (shown.length - 1) ? '  \u2192' : ''}
                        </span>
                      ));
                    })()}
                    <button onClick={() => viewParticipants(c.netting_id)} className={`font-ibm text-[10px] px-2 py-1 rounded-sm border ${isDark ? 'border-zinc-700 text-zinc-300 hover:border-yellow-400/50' : 'border-zinc-200 text-zinc-700 hover:border-yellow-400/50'}`} data-testid={`view-participants-${c.netting_id}`}>View Participants</button>
                  </div>
                  {execErrors[c.netting_id] && (
                    <div className={`mt-3 p-3 rounded-sm border ${isDark ? 'border-red-900 bg-red-500/10' : 'border-red-200 bg-red-50'}`} data-testid={`exec-error-${c.netting_id}`}>
                      <p className={`font-ibm text-xs font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>Execution blocked</p>
                      <p className={`font-ibm text-[11px] mt-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{execErrors[c.netting_id].error}</p>
                      {execErrors[c.netting_id].blocked_user && <p className={`font-ibm text-[11px] mt-0.5 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Blocking user: <strong>{execErrors[c.netting_id].blocked_user.name}</strong>. Other cycles (which don't include this user) can still be executed. Once they re-enable their mandate / fund their account, press Re-execute.</p>}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>{new Date(c.created_at).toLocaleString()}</p>
                    {isProposed && (
                      <button onClick={() => handleExecute(c.netting_id)} className="h-8 px-4 bg-yellow-400 text-zinc-950 font-ibm text-xs font-semibold rounded-sm hover:bg-yellow-500 flex items-center gap-1" data-testid={`execute-${c.netting_id}`}>
                        <Play size={12} weight="bold" /> {execErrors[c.netting_id] ? 'Re-execute' : 'Execute'}
                      </button>
                    )}
                  </div>
                </div>
              );
              return (
                <div className="space-y-6">
                  <div>
                    <h3 className={`font-outfit text-sm font-semibold mb-3 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>Proposed Cycles ({proposed.length}) — review &amp; Execute</h3>
                    <div className="space-y-3">
                      {proposed.length === 0
                        ? <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No proposed cycles. Click "Run Engine" to discover.</p>
                        : proposed.map(c => renderCycle(c, true))}
                    </div>
                  </div>
                  <div>
                    <h3 className={`font-outfit text-sm font-semibold mb-3 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Executed Cycles ({executed.length})</h3>
                    <div className="space-y-3">
                      {executed.length === 0
                        ? <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>None executed yet</p>
                        : executed.map(c => renderCycle(c, false))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          {/* PARTICIPANTS / NODES */}
          <TabsContent value="nodes">
            <div className={`border rounded-md overflow-hidden ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <div className={`grid grid-cols-6 gap-3 p-3 font-ibm text-[10px] font-semibold uppercase tracking-[0.15em] ${isDark ? 'bg-zinc-900 text-zinc-500' : 'bg-zinc-50 text-zinc-500'}`}>
                <span>Name</span><span>Owes</span><span>Owed</span><span>Fee</span><span>Status</span><span>KYC</span>
              </div>
              <div className="max-h-[65vh] overflow-y-auto">
                {nodes.length === 0 ? <p className={`p-6 text-center font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No participants yet</p> :
                  nodes.map(n => (
                    <div key={n.node_id} onClick={() => openUserDetail(n.user_id)} role="button" tabIndex={0} className={`grid grid-cols-6 gap-3 p-3 border-t items-center cursor-pointer transition-colors ${isDark ? 'border-zinc-800 hover:bg-zinc-900' : 'border-zinc-200 hover:bg-zinc-50'}`} data-testid={`node-${n.node_id}`}>
                      <span className={`font-ibm text-sm ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{n.name}</span>
                      <span className={`font-ibm text-xs text-red-400`}>{CUR}{(n.total_liability || 0).toLocaleString()}</span>
                      <span className={`font-ibm text-xs text-green-500`}>{CUR}{(n.total_receivable || 0).toLocaleString()}</span>
                      <span className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{CUR}{(n.amount_payable || 0).toLocaleString()} <span className="text-zinc-500">({n.fee_method})</span></span>
                      <span className={`font-ibm text-xs font-semibold ${feeStatusColor(n.fee_status)}`}>{n.fee_status}</span>
                      <span>{n.bank_verified ? <CheckCircle size={16} className="text-green-500" /> : <span className="font-ibm text-[10px] text-yellow-400">pending</span>}</span>
                    </div>
                  ))}
              </div>
            </div>
          </TabsContent>

          {/* POOL */}
          <TabsContent value="pool">
            <div className={`p-5 border rounded-md mb-4 ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
              <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Pooling Account Balance</p>
              <p className="font-outfit text-4xl font-bold text-yellow-400 mt-1">{CUR}{(pool?.pool?.balance || 0).toLocaleString()}</p>
              <div className="flex gap-2 mt-4 max-w-sm">
                <input value={topupAmount} onChange={e => setTopupAmount(e.target.value)} placeholder="Amount to add" inputMode="numeric"
                  className={`flex-1 h-10 px-3 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-950 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid="pool-topup-input" />
                <button onClick={handleTopup} className="h-10 px-4 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 flex items-center gap-1" data-testid="pool-topup-btn"><Plus size={14} weight="bold" /> Top Up</button>
              </div>
            </div>
            <h3 className={`font-outfit text-base font-semibold mb-3 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Pool Transactions</h3>
            <div className="space-y-2">
              {(pool?.transactions || []).length === 0 ? <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No transactions</p> :
                (pool?.transactions || []).map(t => (
                  <div key={t.pool_txn_id} className={`flex items-center justify-between p-3 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
                    <div>
                      <p className={`font-ibm text-sm ${isDark ? 'text-zinc-50' : 'text-zinc-950'} capitalize`}>{t.type}</p>
                      <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{t.description} · {new Date(t.created_at).toLocaleString()}</p>
                    </div>
                    <span className={`font-outfit text-sm font-bold ${t.amount >= 0 ? 'text-green-500' : 'text-red-400'}`}>{t.amount >= 0 ? '+' : ''}{CUR}{Math.abs(t.amount).toLocaleString()}</span>
                  </div>
                ))}
            </div>
          </TabsContent>

          {/* BALANCES */}
          <TabsContent value="balances">
            <div className={`border rounded-md overflow-hidden ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <div className={`grid grid-cols-3 gap-4 p-3 font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'bg-zinc-900 text-zinc-500' : 'bg-zinc-50 text-zinc-500'}`}>
                <span>User</span><span>Email</span><span>Balance</span>
              </div>
              <div className="max-h-[65vh] overflow-y-auto">
                {balances.map(b => (
                  <div key={b.user_id} className={`grid grid-cols-3 gap-4 p-3 border-t ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`} data-testid={`bal-${b.user_id}`}>
                    <span className={`font-ibm text-sm ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{b.name}</span>
                    <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{b.email}</span>
                    <span className={`font-outfit text-sm font-bold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{CUR}{Math.round(b.units).toLocaleString()}</span>
                  </div>
                ))}
                {balances.length === 0 && <p className={`p-6 text-center font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No balances</p>}
              </div>
            </div>
          </TabsContent>

          {/* LOANS */}
          <TabsContent value="loans">
            <div className={`flex items-center justify-between p-4 border rounded-md mb-4 ${allowLoans ? (isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50') : 'border-red-500/40 bg-red-500/5'}`} data-testid="allow-loans-panel">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${isDark ? 'bg-yellow-400/10' : 'bg-yellow-100'}`}><Bank size={20} weight="bold" className="text-yellow-400" /></div>
                <div>
                  <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Allow Loans</h3>
                  <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Master switch for the bank/NBFC loan feature. When off, the loan box is hidden for all users.</p>
                  <p className={`font-ibm text-xs font-semibold mt-0.5 ${allowLoans ? 'text-green-500' : 'text-red-400'}`} data-testid="allow-loans-status">{allowLoans ? 'Enabled — users can add loans' : 'Disabled — loan box hidden'}</p>
                </div>
              </div>
              <button onClick={toggleLoans} disabled={savingLoanCfg} className={`h-9 px-4 font-ibm text-xs font-semibold rounded-sm disabled:opacity-50 ${allowLoans ? 'bg-red-500 text-zinc-50 hover:bg-red-400' : 'bg-green-500 text-zinc-950 hover:bg-green-400'}`} data-testid="allow-loans-toggle-btn">
                {allowLoans ? 'Turn Off' : 'Turn On'}
              </button>
            </div>

            <h3 className={`font-outfit text-sm font-semibold mb-3 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Submitted Loans ({loans.length})</h3>
            <div className="space-y-2">
              {loans.length === 0 ? <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No loans submitted yet.</p> :
                loans.map(l => {
                  const sc = { in_verification: 'text-yellow-400', verified_locked: 'text-green-500', fee_paid: 'text-blue-400', settling: 'text-yellow-400', closed: 'text-green-500', rejected: 'text-red-400' }[l.status] || 'text-zinc-400';
                  return (
                    <div key={l.loan_id} className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`admin-loan-${l.loan_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{l.institution}</span>
                            <span className={`font-ibm text-[10px] font-semibold uppercase tracking-wider ${sc}`}>{(l.status || '').replace(/_/g, ' ')}</span>
                          </div>
                          <p className={`font-ibm text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{l.user_name} · A/C {l.account_number} · {l.branch_city || '—'}</p>
                          <p className={`font-ibm text-[10px] mt-0.5 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Fee (20%): {CUR}{(l.fee || 0).toLocaleString()}{l.closure ? ` · Paid ref ${l.closure.reference} on ${l.closure.paid_date} ${l.closure.paid_time}` : ''}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-outfit text-lg font-bold text-yellow-400">{CUR}{(l.loan_amount || 0).toLocaleString()}</p>
                          {l.status === 'settling' && (
                            <button onClick={() => openPayLoan(l)} className="mt-1 h-8 px-3 bg-yellow-400 text-zinc-950 font-ibm text-xs font-semibold rounded-sm hover:bg-yellow-500 flex items-center gap-1" data-testid={`mark-paid-${l.loan_id}`}>
                              <CheckCircle size={12} weight="bold" /> Mark Paid
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </TabsContent>

          {/* REFUNDS */}
          <TabsContent value="refunds">
            <h3 className={`font-outfit text-sm font-semibold mb-3 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Refunds ({refunds.length})</h3>
            <div className="space-y-2">
              {refunds.length === 0 ? <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No refunds yet.</p> :
                refunds.map(r => {
                  const done = r.status === 'completed';
                  return (
                    <div key={r.refund_id} className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`admin-refund-${r.refund_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{r.user_name}</span>
                            <span className={`font-ibm text-[10px] font-semibold uppercase tracking-wider ${done ? 'text-green-500' : 'text-blue-400'}`}>{done ? 'Completed' : 'In Process'}</span>
                          </div>
                          <p className={`font-ibm text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{r.reason}</p>
                          <p className={`font-ibm text-[10px] mt-0.5 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{r.eligible_at ? `Eligible: ${new Date(r.eligible_at).toLocaleDateString()}` : ''}{done && r.completion ? ` · Paid ref ${r.completion.reference}` : ''}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-outfit text-lg font-bold text-blue-400">{CUR}{(r.amount || 0).toLocaleString()}</p>
                          {!done && (
                            <button onClick={() => openRefund(r)} className="mt-1 h-8 px-3 bg-yellow-400 text-zinc-950 font-ibm text-xs font-semibold rounded-sm hover:bg-yellow-500 flex items-center gap-1" data-testid={`confirm-refund-${r.refund_id}`}>
                              <CheckCircle size={12} weight="bold" /> Confirm Refund
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </TabsContent>

          {/* MAINTENANCE */}
          <TabsContent value="maintenance">           <div className={`max-w-xl p-5 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid="maintenance-panel">
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${isDark ? 'bg-yellow-400/10' : 'bg-yellow-100'}`}><Wrench size={20} weight="bold" className="text-yellow-400" /></div>
                <div>
                  <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Maintenance Mode</h3>
                  <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Freezes the platform for all non-admin users and shows them a "Under Maintenance" page with the date & time you set.</p>
                </div>
              </div>

              <div className={`flex items-center justify-between p-3 rounded-sm border mb-4 ${maint.enabled ? 'border-red-500/40 bg-red-500/5' : (isDark ? 'border-zinc-800' : 'border-zinc-200')}`}>
                <div>
                  <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>Status</p>
                  <p className={`font-ibm text-xs font-semibold ${maint.enabled ? 'text-red-400' : 'text-green-500'}`} data-testid="maintenance-status-label">{maint.enabled ? 'ENABLED — users frozen' : 'Disabled — platform live'}</p>
                </div>
                <button
                  onClick={() => handleSaveMaintenance({ enabled: !maint.enabled })}
                  disabled={savingMaint}
                  className={`h-9 px-4 font-ibm text-xs font-semibold rounded-sm disabled:opacity-50 ${maint.enabled ? 'bg-green-500 text-zinc-950 hover:bg-green-400' : 'bg-red-500 text-zinc-50 hover:bg-red-400'}`}
                  data-testid="maintenance-toggle-btn">
                  {maint.enabled ? 'Turn Off' : 'Turn On'}
                </button>
              </div>

              <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Expected back online (Date & Time)</label>
              <input type="datetime-local" value={maint.until} onChange={e => setMaint({ ...maint, until: e.target.value })}
                className={`w-full h-10 px-3 mt-1 mb-4 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-950 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid="maintenance-until-input" />

              <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Message to users (optional)</label>
              <textarea value={maint.message} onChange={e => setMaint({ ...maint, message: e.target.value })} rows={3}
                placeholder="We are upgrading the netting engine. The platform will be back shortly."
                className={`w-full px-3 py-2 mt-1 mb-4 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-950 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid="maintenance-message-input" />

              <button onClick={() => handleSaveMaintenance()} disabled={savingMaint}
                className="h-10 px-5 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 disabled:opacity-50" data-testid="maintenance-save-btn">
                {savingMaint ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      {participantsOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" data-testid="participants-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setParticipantsOpen(null)} />
          <div className={`relative w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-md border ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <div className={`sticky top-0 p-4 border-b flex items-center justify-between ${isDark ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-white'}`}>
              <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Cycle Participants {participantsData ? `(${participantsData.participants_count})` : ''}</h3>
              <button onClick={() => setParticipantsOpen(null)} className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`} data-testid="close-participants">Close</button>
            </div>
            <div className="p-4">
              {!participantsData ? <div className="flex justify-center py-10"><div className="animate-spin w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full" /></div> : (
                <div className="space-y-2">
                  {participantsData.details.map((p, i) => (
                    <div key={p.user_id} onClick={() => openUserDetail(p.user_id)} role="button" tabIndex={0} className={`p-3 border rounded-md cursor-pointer transition-colors ${isDark ? 'border-zinc-800 bg-zinc-900 hover:border-yellow-400/50' : 'border-zinc-200 bg-zinc-50 hover:border-yellow-400/50'}`} data-testid={`participant-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`font-outfit text-sm font-bold ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>{i + 1}</span>
                          <div>
                            <p className={`font-ibm text-sm font-medium ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{p.name} {p.is_endpoint && <span className="text-green-500 text-[10px]">(endpoint · paid)</span>}</p>
                            <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{p.email} · {p.phone || 'no phone'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>Fee {CUR}{(p.amount_payable || 0).toLocaleString()} <span className="text-zinc-500">({p.fee_method})</span></p>
                          <p className={`font-ibm text-[10px] ${feeStatusColor(p.fee_status)}`}>{p.fee_status} · {p.bank_verified ? 'KYC ✓' : 'KYC pending'}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex gap-4">
                          <span className="font-ibm text-[10px] text-red-400">Total dues {CUR}{(p.total_liability || 0).toLocaleString()}</span>
                          <span className="font-ibm text-[10px] text-green-500">Total receivables {CUR}{(p.total_receivable || 0).toLocaleString()}</span>
                        </div>
                        <span className="font-ibm text-[10px] text-yellow-400">View profile →</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User detail drill-down (read-only) */}
      {userDetailOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-6" data-testid="user-detail-modal">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setUserDetailOpen(null)} />
          <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-md border ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <div className={`sticky top-0 p-4 border-b flex items-center justify-between ${isDark ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-white'}`}>
              <div className="flex items-center gap-2">
                <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{userDetail?.profile?.name || 'User'}</h3>
                <span className="font-ibm text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-500/10 text-zinc-400">View only</span>
              </div>
              <button onClick={() => setUserDetailOpen(null)} className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`} data-testid="close-user-detail">Close</button>
            </div>
            <div className="p-4">
              {!userDetail ? <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full" /></div> : (
                <div className="space-y-5">
                  {/* Profile */}
                  <div className={`p-3 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid="user-detail-profile">
                    <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Profile</p>
                    <div className="grid grid-cols-2 gap-y-1.5 gap-x-4">
                      <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Email</span>
                      <span className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{userDetail.profile.email || '—'}</span>
                      <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Phone</span>
                      <span className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{userDetail.profile.phone || '—'}</span>
                      <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>KYC / Bank</span>
                      <span className={`font-ibm text-xs ${userDetail.profile.bank_verified ? 'text-green-500' : 'text-yellow-400'}`}>{userDetail.profile.bank_verified ? 'Verified' : 'Pending'}{userDetail.bank ? ` · ${userDetail.bank.bank_name || ''} ${userDetail.bank.ifsc || ''}` : ''}</span>
                      <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Balance</span>
                      <span className={`font-ibm text-xs font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{CUR}{(userDetail.balance || 0).toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className={`p-2 rounded-sm ${isDark ? 'bg-zinc-950' : 'bg-white'} border ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                        <p className="font-ibm text-[10px] uppercase tracking-wider text-zinc-500">Total Dues</p>
                        <p className="font-outfit text-base font-bold text-red-400">{CUR}{(userDetail.total_dues || 0).toLocaleString()}</p>
                      </div>
                      <div className={`p-2 rounded-sm ${isDark ? 'bg-zinc-950' : 'bg-white'} border ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                        <p className="font-ibm text-[10px] uppercase tracking-wider text-zinc-500">Total Receivables</p>
                        <p className="font-outfit text-base font-bold text-green-500">{CUR}{(userDetail.total_receivables || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Transactions */}
                  <div data-testid="user-detail-transactions">
                    <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Transaction History ({userDetail.transactions.length})</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {userDetail.transactions.length === 0 ? <p className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No transactions</p> :
                        userDetail.transactions.map((t, i) => (
                          <div key={i} className={`flex items-center justify-between p-2 border rounded-sm ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
                            <div>
                              <p className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'} capitalize`}>{(t.type || '').replace(/_/g, ' ')}</p>
                              <p className="font-ibm text-[10px] text-zinc-500">{t.description || ''}{t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString()}` : ''}</p>
                            </div>
                            <span className="font-outfit text-xs font-bold text-green-500">{CUR}{(t.amount || 0).toLocaleString()}</span>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Notifications */}
                  <div data-testid="user-detail-notifications">
                    <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Notifications ({userDetail.notifications.length})</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {userDetail.notifications.length === 0 ? <p className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No notifications</p> :
                        userDetail.notifications.map((n, i) => (
                          <div key={i} className={`p-2 border rounded-sm ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
                            <p className={`font-ibm text-xs font-medium ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{n.title}</p>
                            <p className="font-ibm text-[10px] text-zinc-500">{n.message}{n.created_at ? ` · ${new Date(n.created_at).toLocaleDateString()}` : ''}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mark loan paid (Certificate of Loan Closure) */}
      {payLoan && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6" data-testid="pay-loan-modal">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setPayLoan(null)} />
          <div className={`relative w-full max-w-md max-h-[92vh] overflow-y-auto rounded-md border ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <div className={`sticky top-0 p-4 border-b flex items-center justify-between ${isDark ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-white'}`}>
              <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Record Loan Payoff</h3>
              <button onClick={() => setPayLoan(null)} className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`} data-testid="close-pay-loan">Close</button>
            </div>
            <div className="p-4 space-y-3">
              <div className={`p-3 rounded-sm border ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
                <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{payLoan.institution} · {CUR}{(payLoan.loan_amount || 0).toLocaleString()}</p>
                <p className={`font-ibm text-[11px] ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{payLoan.user_name} · A/C {payLoan.account_number}</p>
              </div>
              {[
                { k: 'amount', label: 'Amount Paid', type: 'number', testid: 'pay-amount' },
                { k: 'reference', label: 'Transaction Reference / UTR *', type: 'text', testid: 'pay-reference' },
                { k: 'particulars', label: 'Particulars / Remarks', type: 'text', testid: 'pay-particulars' },
                { k: 'mode', label: 'Payment Mode', type: 'text', testid: 'pay-mode' },
              ].map(f => (
                <div key={f.k}>
                  <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{f.label}</label>
                  <input type={f.type} value={payForm[f.k]} onChange={e => setPayForm({ ...payForm, [f.k]: e.target.value })}
                    className={`w-full h-10 px-3 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid={f.testid} />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Date</label>
                  <input type="date" value={payForm.paid_date} onChange={e => setPayForm({ ...payForm, paid_date: e.target.value })} className={`w-full h-10 px-3 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid="pay-date" />
                </div>
                <div>
                  <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Time</label>
                  <input type="time" value={payForm.paid_time} onChange={e => setPayForm({ ...payForm, paid_time: e.target.value })} className={`w-full h-10 px-3 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid="pay-time" />
                </div>
              </div>
              <button onClick={submitPayLoan} disabled={payingLoan} className="w-full h-11 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-pay-loan">
                {payingLoan ? 'Closing...' : 'Confirm Payoff & Issue Certificate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm refund modal */}
      {refundItem && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6" data-testid="refund-modal">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setRefundItem(null)} />
          <div className={`relative w-full max-w-md max-h-[92vh] overflow-y-auto rounded-md border ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <div className={`sticky top-0 p-4 border-b flex items-center justify-between ${isDark ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-white'}`}>
              <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Confirm Refund</h3>
              <button onClick={() => setRefundItem(null)} className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`} data-testid="close-refund">Close</button>
            </div>
            <div className="p-4 space-y-3">
              <div className={`p-3 rounded-sm border ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
                <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{refundItem.user_name} · {CUR}{(refundItem.amount || 0).toLocaleString()}</p>
                <p className={`font-ibm text-[11px] ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{refundItem.reason}</p>
              </div>
              {[
                { k: 'amount', label: 'Amount Refunded', type: 'number', testid: 'refund-amount' },
                { k: 'reference', label: 'Transaction Reference / UTR *', type: 'text', testid: 'refund-reference' },
                { k: 'particulars', label: 'Particulars / Remarks', type: 'text', testid: 'refund-particulars' },
                { k: 'mode', label: 'Payment Mode', type: 'text', testid: 'refund-mode' },
              ].map(f => (
                <div key={f.k}>
                  <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{f.label}</label>
                  <input type={f.type} value={refundForm[f.k]} onChange={e => setRefundForm({ ...refundForm, [f.k]: e.target.value })}
                    className={`w-full h-10 px-3 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid={f.testid} />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Date</label>
                  <input type="date" value={refundForm.paid_date} onChange={e => setRefundForm({ ...refundForm, paid_date: e.target.value })} className={`w-full h-10 px-3 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid="refund-date" />
                </div>
                <div>
                  <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Time</label>
                  <input type="time" value={refundForm.paid_time} onChange={e => setRefundForm({ ...refundForm, paid_time: e.target.value })} className={`w-full h-10 px-3 rounded-sm border font-ibm text-sm ${isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid="refund-time" />
                </div>
              </div>
              <button onClick={submitRefund} disabled={confirmingRefund} className="w-full h-11 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-refund">
                {confirmingRefund ? 'Confirming...' : 'Confirm Refund Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
